import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, RefreshCw, AlertTriangle, ChevronRight } from "lucide-react";
import { FinancialHealthCard } from "@/components/admin/ledger/FinancialHealthCard";
import {
  fetchMonthlyReport, fetchAllTimeReport, fetchMonthlyHistory,
  buildMonthOptions, money,
  type MonthlyReport, type AllTimeReport, type MonthlyHistoryRow,
} from "@/lib/financialReport";

type Mode = "month" | "all_time";
type Drill = null | "revenue" | "payroll" | "grace";

const HARD_FLOOR = "2026-07-01";

function currentMonthKey(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export default function ReportsPage() {
  const [mode, setMode] = useState<Mode>("month");
  const [month, setMonth] = useState<string>(currentMonthKey());
  const [monthly, setMonthly] = useState<MonthlyReport | null>(null);
  const [allTime, setAllTime] = useState<AllTimeReport | null>(null);
  const [history, setHistory] = useState<MonthlyHistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drill, setDrill] = useState<Drill>(null);

  const monthOptions = useMemo(() => buildMonthOptions(HARD_FLOOR), []);

  async function load() {
    setLoading(true); setError(null);
    try {
      if (mode === "month") {
        const r = await fetchMonthlyReport({ period_type: "month", month });
        setMonthly(r);
      } else {
        const r = await fetchAllTimeReport(false);
        setAllTime(r);
      }
    } catch (e) {
      setError((e as Error).message ?? "Financial data unavailable");
    } finally {
      setLoading(false);
    }
  }

  async function loadHistory() {
    setHistoryLoading(true);
    try {
      const r = await fetchMonthlyHistory(false);
      setHistory(r.months);
    } catch (e) {
      // Non-fatal — history is a secondary view.
      console.warn("history load", e);
    } finally {
      setHistoryLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, month]);
  useEffect(() => { loadHistory(); }, []);

  const active = mode === "month" ? monthly : allTime;

  return (
    <AdminShell section="reports">
      <div className="container mx-auto p-4 md:p-6 space-y-5">
        <FinancialHealthCard />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Financial Reports</h1>
            <p className="text-sm text-muted-foreground">
              Actual revenue, payroll, Grace share, and monthly net results. Every total comes from the authoritative
              records — nothing here is projected from hypothetical enrollment.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href="/admin/ledger">Open Financial Ledger</a>
            </Button>
            <Select value={mode} onValueChange={(v) => setMode(v as Mode)}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month">This month</SelectItem>
                <SelectItem value="all_time">All time</SelectItem>
              </SelectContent>
            </Select>
            {mode === "month" && (
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {monthOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {active?.period && (
          <p className="text-xs text-muted-foreground">
            Reporting window: <strong>{active.period.start_date}</strong> → <strong>{active.period.end_date}</strong>
            {" · "}Reporting begins <strong>{active.period.cutoff}</strong>.
          </p>
        )}

        {error && (
          <Card className="border-destructive/60">
            <CardHeader><CardTitle className="text-destructive text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Financial data unavailable
            </CardTitle></CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="text-muted-foreground">{error}</div>
              <Button size="sm" onClick={load}>Retry</Button>
            </CardContent>
          </Card>
        )}

        {loading && !active && (
          <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        )}

        {mode === "month" && monthly && !error && (
          <MonthlyView report={monthly} onDrill={setDrill} />
        )}
        {mode === "all_time" && allTime && !error && (
          <AllTimeView report={allTime} onDrill={setDrill} />
        )}

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Monthly history</CardTitle>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : history.length === 0 ? (
              <div className="text-sm text-muted-foreground">No months to display.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">Refunds</TableHead>
                      <TableHead className="text-right">Payroll paid</TableHead>
                      <TableHead className="text-right">Payroll projected</TableHead>
                      <TableHead className="text-right">Grace</TableHead>
                      <TableHead className="text-right">Net</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((r) => (
                      <TableRow
                        key={r.month}
                        className="cursor-pointer"
                        onClick={() => { setMode("month"); setMonth(r.month); }}
                      >
                        <TableCell>{r.label}</TableCell>
                        <TableCell className="text-right tabular-nums">{money(r.revenue_cents)}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{money(r.refund_cents)}</TableCell>
                        <TableCell className="text-right tabular-nums">{money(r.payroll_paid_cents)}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{money(r.payroll_projected_cents)}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{money(r.grace_accrued_cents)}</TableCell>
                        <TableCell className={`text-right tabular-nums font-medium ${r.net_operating_cents < 0 ? "text-red-700" : "text-emerald-700"}`}>{money(r.net_operating_cents)}</TableCell>
                        <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <DrillDialog
          drill={drill}
          onClose={() => setDrill(null)}
          detail={active?.detail}
        />
      </div>
    </AdminShell>
  );
}

/* ---------------- Views ---------------- */

function Stat({ label, value, hint, tone = "default", onClick }: {
  label: string; value: string; tone?: "default" | "positive" | "negative" | "muted";
  hint?: string;
  onClick?: () => void;
}) {
  const color =
    tone === "positive" ? "text-emerald-700"
    : tone === "negative" ? "text-red-700"
    : tone === "muted" ? "text-muted-foreground"
    : "text-foreground";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className="text-left rounded-md border border-border/70 bg-background px-3 py-2.5 hover:border-primary/60 transition disabled:hover:border-border/70 min-w-0"
    >
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground leading-relaxed">{label}</div>
      <div className={`text-lg tabular-nums font-medium ${color}`}>{value}</div>
      {hint && <div className="mt-1 text-[11px] leading-snug text-muted-foreground">{hint}</div>}
    </button>
  );
}

function MonthlyView({ report, onDrill }: { report: MonthlyReport; onDrill: (d: "revenue" | "payroll" | "grace") => void }) {
  const s = report.summary;
  const netTone = s.net_operating_cents < 0 ? "negative" : "positive";
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{report.period.label}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Stat label="Revenue received" value={money(s.revenue_collected_cents)} hint="Collected during this period" onClick={() => onDrill("revenue")} />
          <Stat label="Refunds sent" value={money(s.refund_cents)} hint="Returned during this period" tone="muted" onClick={() => onDrill("revenue")} />
          <Stat label="Net registration revenue" value={money(s.net_revenue_cents)} hint="Received minus refunds" onClick={() => onDrill("revenue")} />
          <Stat label="Paid registrations" value={String(s.paid_registration_count)} hint="Registrations in this period" tone="muted" onClick={() => onDrill("revenue")} />
          <Stat label="Payroll already paid" value={money(s.payroll_paid_cents)} hint="Confirmed instructor payments" onClick={() => onDrill("payroll")} />
          <Stat label="Payroll still projected" value={money(s.payroll_projected_cents)} hint="Scheduled but not yet paid" tone="muted" onClick={() => onDrill("payroll")} />
          <Stat label="Total payroll cost" value={money(s.total_payroll_cents)} hint="Paid plus projected payroll" onClick={() => onDrill("payroll")} />
          <Stat label={`Grace share accrued (${s.grace_percent}%)`} value={money(s.grace_accrued_cents)} hint="Facility share for this period" tone="muted" onClick={() => onDrill("grace")} />
        </div>
        <div className="rounded-md border border-border bg-muted/40 px-4 py-3 flex items-baseline justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Net Operating Amount</div>
            <div className="text-[11px] text-muted-foreground">Net revenue − Total payroll − Grace accrued</div>
          </div>
          <div className={`text-2xl tabular-nums font-semibold ${netTone === "negative" ? "text-red-700" : "text-emerald-700"}`}>
            {money(s.net_operating_cents)}
          </div>
        </div>
        {report.needs_review?.length > 0 && (
          <div className="rounded-md border border-amber-300/70 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <div className="font-medium mb-1">Items needing review</div>
            <ul className="list-disc pl-5 space-y-0.5">
              {report.needs_review.map((r: any, i: number) => (
                <li key={i}>{r.instructor_name ? `${r.instructor_name} — ` : ""}{r.reason}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AllTimeView({ report, onDrill }: { report: AllTimeReport; onDrill: (d: "revenue" | "payroll" | "grace") => void }) {
  const s = report.summary;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">All time since {report.period.cutoff}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Stat label="Total Revenue Collected" value={money(s.revenue_collected_cents)} onClick={() => onDrill("revenue")} />
          <Stat label="Total Refunds" value={money(s.refund_cents)} tone="muted" onClick={() => onDrill("revenue")} />
          <Stat label="Net Revenue" value={money(s.net_revenue_cents)} onClick={() => onDrill("revenue")} />
          <Stat label="Paid Registrations" value={String(s.paid_registration_count)} tone="muted" />
          <Stat label="Total Payroll Paid" value={money(s.payroll_paid_cents)} onClick={() => onDrill("payroll")} />
          <Stat label="Current Outstanding Projected Payroll" value={money(s.outstanding_projected_payroll_cents)} tone="muted" onClick={() => onDrill("payroll")} />
          <Stat label="Grace Share Paid" value={money(s.grace_paid_cents)} onClick={() => onDrill("grace")} />
          <Stat label="Grace Accrued but Unpaid" value={money(s.grace_accrued_unpaid_cents)} tone="muted" onClick={() => onDrill("grace")} />
          <Stat label="Revenue-Generating Classes" value={String(s.revenue_generating_class_count)} tone="muted" />
        </div>
        <div className="rounded-md border border-border bg-muted/40 px-4 py-3 flex items-baseline justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Net Operating Amount (all time)</div>
            <div className="text-[11px] text-muted-foreground">Net revenue − payroll − Grace share accrued</div>
          </div>
          <div className={`text-2xl tabular-nums font-semibold ${s.net_operating_cents < 0 ? "text-red-700" : "text-emerald-700"}`}>
            {money(s.net_operating_cents)}
          </div>
        </div>
        {report.needs_review?.length > 0 && (
          <div className="rounded-md border border-amber-300/70 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <div className="font-medium mb-1">Items needing review</div>
            <ul className="list-disc pl-5 space-y-0.5">
              {report.needs_review.map((r: any, i: number) => (
                <li key={i}>{r.instructor_name ? `${r.instructor_name} — ` : ""}{r.reason}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------------- Drill-down ---------------- */

function DrillDialog({ drill, onClose, detail }: {
  drill: Drill; onClose: () => void;
  detail: MonthlyReport["detail"] | undefined;
}) {
  if (!drill || !detail) return null;

  const title =
    drill === "revenue" ? "Revenue detail"
    : drill === "payroll" ? "Payroll detail"
    : "Grace share detail";

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>All rows contributing to the summary total. Reconciles exactly.</DialogDescription>
        </DialogHeader>
        {drill === "revenue" && <RevenueDetail detail={detail} />}
        {drill === "payroll" && <PayrollDetail detail={detail} />}
        {drill === "grace" && <GraceDetail detail={detail} />}
      </DialogContent>
    </Dialog>
  );
}

function RevenueDetail({ detail }: { detail: MonthlyReport["detail"] }) {
  const included = detail.revenue.included;
  const excluded = detail.revenue.excluded;
  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      <div>
        <div className="text-xs text-muted-foreground mb-1">Included ({included.length})</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead><TableHead>Class</TableHead><TableHead>Payer</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Refund</TableHead>
              <TableHead className="text-right">Net</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {included.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="text-xs">{r.payment_date?.slice(0, 10)}</TableCell>
                <TableCell className="text-xs">{r.class_id}</TableCell>
                <TableCell className="text-xs">{r.payer}{r.child ? ` · ${r.child}` : ""}</TableCell>
                <TableCell className="text-right tabular-nums text-xs">{money(r.amount_cents)}</TableCell>
                <TableCell className="text-right tabular-nums text-xs text-muted-foreground">{money(r.refund_cents)}</TableCell>
                <TableCell className="text-right tabular-nums text-xs">{money(r.net_cents)}</TableCell>
              </TableRow>
            ))}
            {included.length === 0 && <TableRow><TableCell colSpan={6} className="text-xs text-muted-foreground text-center">No revenue rows in this window.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      {excluded.length > 0 && (
        <div>
          <div className="text-xs text-muted-foreground mb-1">Excluded ({excluded.length})</div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Class</TableHead><TableHead>Reason</TableHead><TableHead className="text-right">Amount</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {excluded.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs">{r.class_id}</TableCell>
                  <TableCell className="text-xs">{r.reason}</TableCell>
                  <TableCell className="text-right tabular-nums text-xs">{money(r.amount_cents)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function PayrollDetail({ detail }: { detail: MonthlyReport["detail"] }) {
  const paid = detail.payroll_paid;
  const projected = detail.payroll_projected;
  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      <div>
        <div className="text-xs text-muted-foreground mb-1">Paid ({paid.length})</div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Paid</TableHead><TableHead>Instructor</TableHead><TableHead>Classes / Coverage</TableHead>
            <TableHead>Method</TableHead><TableHead className="text-right">Amount</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {paid.map((r: any) => (
              <TableRow key={r.id}>
                <TableCell className="text-xs">{r.paid_at}</TableCell>
                <TableCell className="text-xs">{r.instructor_name}</TableCell>
                <TableCell className="text-xs">
                  {Array.isArray(r.classes) && r.classes.length > 0
                    ? r.classes.map((c: any) => c.class_slug ?? c.slug ?? c.class_id ?? c).join(", ")
                    : "—"}
                </TableCell>
                <TableCell className="text-xs">{r.payment_method}</TableCell>
                <TableCell className="text-right tabular-nums text-xs">{money(r.amount_cents)}</TableCell>
              </TableRow>
            ))}
            {paid.length === 0 && <TableRow><TableCell colSpan={5} className="text-xs text-muted-foreground text-center">No payroll paid in this window.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <div>
        <div className="text-xs text-muted-foreground mb-1">Projected ({projected.length})</div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Pay Date</TableHead><TableHead>Instructor</TableHead><TableHead>Coverage</TableHead>
            <TableHead>Meetings</TableHead><TableHead className="text-right">Amount</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {projected.map((r: any, i: number) => (
              <TableRow key={i}>
                <TableCell className="text-xs">{r.pay_date}</TableCell>
                <TableCell className="text-xs">{r.instructor_name}</TableCell>
                <TableCell className="text-xs">
                  {r.coverage
                    ? `${r.coverage.start ?? ""} → ${r.coverage.end ?? ""}`
                    : r.schedule_label ?? "Current period"}
                </TableCell>
                <TableCell className="text-xs">{r.meeting_count ?? (Array.isArray(r.meetings) ? r.meetings.length : "—")}</TableCell>
                <TableCell className="text-right tabular-nums text-xs">{money(r.amount_cents)}</TableCell>
              </TableRow>
            ))}
            {projected.length === 0 && <TableRow><TableCell colSpan={5} className="text-xs text-muted-foreground text-center">No projected payroll in this window.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function GraceDetail({ detail }: { detail: MonthlyReport["detail"] }) {
  const payments = detail.grace_payments;
  return (
    <div className="space-y-3 max-h-[70vh] overflow-y-auto">
      <div className="text-xs text-muted-foreground">
        Grace Community Church receives 10% of net qualifying registration revenue. Accrual = 10% of Net Revenue in
        this period. Payments below reduce the accrued-but-unpaid balance.
      </div>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Paid</TableHead><TableHead>Period</TableHead><TableHead>Method</TableHead>
          <TableHead>Reference</TableHead><TableHead className="text-right">Amount</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {payments.map((p: any) => (
            <TableRow key={p.id}>
              <TableCell className="text-xs">{p.paid_at}</TableCell>
              <TableCell className="text-xs">{p.period_label ?? `${p.period_start} → ${p.period_end}`}</TableCell>
              <TableCell className="text-xs">{p.payment_method ?? "—"}</TableCell>
              <TableCell className="text-xs">{p.reference ?? "—"}</TableCell>
              <TableCell className="text-right tabular-nums text-xs">{money(p.amount_cents)}</TableCell>
            </TableRow>
          ))}
          {payments.length === 0 && <TableRow><TableCell colSpan={5} className="text-xs text-muted-foreground text-center">No Grace payments recorded in this window.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}
